import React, { useEffect, useState } from 'react';
import { watchDoc } from '../../firestoreLive';
import { CopyPill } from './CopyPill';
import { PERSONAL_INFO, SOCIAL_LINKS } from '../../constants';
import { ProfileData } from '../../types';
import MagneticV2 from './MagneticV2';
import { RevealLines, FadeIn } from './RevealText';
import VelocityMarquee from '../VelocityMarquee';
import MarqueeWord from './MarqueeWord';
import ArrowMark from './ArrowMark';
import { trackInteraction } from '../../analytics';

/**
 * Closing set piece: a dark rounded card with a giant invitation,
 * a magnetic email pill, and the socials. Mirrors the hero's framing.
 */
const ContactSection: React.FC = () => {
  const [profile, setProfile] = useState<ProfileData>({ ...PERSONAL_INFO, bio: '', socials: SOCIAL_LINKS });

  useEffect(() => {
    const unsub = watchDoc<ProfileData>('content', 'profile', (d) => {
      setProfile((prev) => ({ ...prev, ...d }));
    });
    return () => unsub();
  }, []);

  const socials = (profile.socials?.length ? profile.socials : SOCIAL_LINKS).filter((s) =>
    ['linkedin', 'vimeo', 'behance', 'dribbble'].includes(s.name.toLowerCase())
  );

  return (
    <section id="contact" className="relative p-2.5 md:p-4">
      <div data-surface="dark"
        // The docked player floats over the bottom-right corner of the viewport.
        // Without this the copyright line sits underneath it on a phone.
        className="relative rounded-[24px] md:rounded-[36px] bg-card overflow-hidden px-6 md:px-14 pt-28 md:pt-32 pb-40 md:pb-12">
        {/* glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 70% 55% at 50% 115%, rgba(46,48,255,0.22), transparent 62%)' }}
        />

        <div className="relative">
          {/* The section number sits right, not left: the fixed logo occupies
              the top-left corner and collided with it here. This also matches
              how every other section labels itself. */}
          <div className="flex items-start justify-between gap-6 mb-12 md:mb-16">
            <h2>
              <RevealLines
                lines={['Have something', 'worth animating?']}
                lineClassName="font-grotesk font-semibold text-[12vw] md:text-[7vw] leading-[1.02] tracking-tight text-white"
              />
            </h2>
            <span className="hidden md:block font-grotesk text-xs uppercase tracking-[0.3em] text-white/55 whitespace-nowrap pt-3">
              004 · Next project
            </span>
          </div>

          <FadeIn>
            {/* Two peers, not a primary with an afterthought underneath:
                same height and shape, weighted by fill rather than by size. */}
            <div className="flex flex-wrap items-center gap-3 md:gap-4">
              <MagneticV2 strength={0.25}>
                <CopyPill
                  href={`mailto:${profile.email}`}
                  value={profile.email}
                  label={profile.email}
                  cursor="SAY HI"
                  onCopied={() => trackInteraction('email_copy')}
                  className="inline-flex items-center gap-4 h-14 md:h-20 px-8 md:px-12 rounded-full font-grotesk font-semibold text-lg md:text-2xl tracking-tight transition-colors duration-300 bg-white text-ink hover:bg-electric hover:text-white"
                  // held on the accent for the length of the confirmation, which
                  // is the pill's own hover state rather than a new colour
                  copiedClassName="!bg-electric !text-white"
                  trail={<ArrowMark dir="right" className={'text-electric'} />}
                />
              </MagneticV2>

              {profile.phone && (
                <MagneticV2 strength={0.25}>
                  <CopyPill
                    href={`tel:${profile.phone.replace(/[^+\d]/g, '')}`}
                    value={profile.phone}
                    label={profile.phone}
                    cursor="CALL"
                    onCopied={() => trackInteraction('phone_copy')}
                    className="group inline-flex items-center gap-3 h-14 md:h-20 px-7 md:px-10 rounded-full border font-grotesk font-medium text-base md:text-xl tracking-tight transition-colors duration-300 border-white/25 text-white hover:bg-white hover:text-ink hover:border-white"
                    copiedClassName="!bg-white !text-ink !border-white"
                    lead={
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                        className="opacity-60 group-hover:opacity-100 transition-opacity"
                      >
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                    }
                  />
                </MagneticV2>
              )}
            </div>
          </FadeIn>

          {/* closing running line, hover to slow it */}
          <div className="-mx-6 md:-mx-14 mt-20 md:mt-28 border-y border-white/10 py-4 md:py-6">
            <VelocityMarquee speed={68} className="select-none">
              {["Let's talk", "Let's create", 'Available for work'].map((w) => (
                <MarqueeWord key={w} tone="light">
                  {w}
                </MarqueeWord>
              ))}
            </VelocityMarquee>
          </div>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mt-14 md:mt-20 pt-8 border-t border-white/10">
            <div className="flex flex-wrap gap-x-8 gap-y-0 md:gap-y-3">
              {socials.map((s) => (
                <a
                  key={s.name}
                  href={s.url}
                  onClick={() => trackInteraction('social_click', s.name)}
                  data-cursor="VISIT"
                  target="_blank"
                  rel="noreferrer"
                  // 16px of text is not a tap target. The min-height gives a thumb 44px
                  // to aim at without changing how the row looks.
                  className="inline-flex items-center min-h-[44px] font-grotesk text-xs md:text-sm uppercase tracking-[0.2em] text-white/55 hover:text-white transition-colors duration-300"
                >
                  {s.name}
                  <ArrowMark className="ml-1.5 opacity-70" />
                </a>
              ))}
            </div>
            <div className="font-grotesk text-[10px] md:text-xs uppercase tracking-[0.25em] text-white/35">
              © 2026 {profile.name} · Designed & built in motion
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
